import { GroupsSortBy, GroupsSortField, GroupTypes } from "@custom-types/group-types";
import { SortItem } from "@custom-types/sort-types";
import { isGroupDetails } from "@custom-types/type-guards";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DEFAULT_GROUPS_SORT_BY, GroupHeaders } from "@components/groups/groups-constants";

/** Defines the minimum length for the group name */
export const MIN_GROUP_NAME_LENGTH = 1;

/** Returns true if a group should have a demo label. */
export function isGroupDemo(
  group: FaroCoreApiTypes.GroupSummaryResponse
): boolean {
  return group.name === "Demo";
}

/** Get number of projects in a group */
export function getProjectNumbersInGroup(group: GroupTypes): number {
  if (isGroupDetails(group)) {
    return group.projects.length;
  }
  return group.numProjects ?? 0;
}

/**
 * Gets the member count for a group
 * @param group - The group to get the member count from
 * @returns The number of members in the group, or -1 if the member count cannot be determined
 * @remarks
 * - For GroupSummaryResponse: returns the numManagers property if available
 * - For GroupDetailResponse/LegacyGroupDetailResponse: returns the length of the members array
 * - Returns -1 if the member count cannot be determined (properties not found or undefined)
 */
function getMemberCount(group: GroupTypes): number {
  if ("numManagers" in group && group.numManagers !== undefined) {
    return group.numManagers;
  }
  if ("members" in group && Array.isArray(group.members)) {
    return group.members.length;
  }
  return -1;
}

/**
 * Sorts an array of groups based on the provided sorting criteria
 * @param groups - The array of groups to sort
 * @param sortBy - The sorting criteria
 * @returns A new sorted array of groups
 */
export function sortGroups(groups: GroupTypes[], sortBy: GroupsSortBy): GroupTypes[] {
  const sortedGroups = [...groups];
  sortedGroups.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case "name_ascending":
        comparison = a.name.localeCompare(b.name);
        break;
      case "name_descending":
        comparison = b.name.localeCompare(a.name);
        break;
      case "members_ascending": {
        const aMemberCount = getMemberCount(a);
        const bMemberCount = getMemberCount(b);
        comparison = aMemberCount - bMemberCount;
        break;
      }
      case "members_descending": {
        const aMemberCount = getMemberCount(a);
        const bMemberCount = getMemberCount(b);
        comparison = bMemberCount - aMemberCount;
        break;
      }
      case "projects_ascending": {
        const aProjectCount = getProjectNumbersInGroup(a);
        const bProjectCount = getProjectNumbersInGroup(b);
        comparison = aProjectCount - bProjectCount;
        break;
      }
      case "projects_descending": {
        const aProjectCount = getProjectNumbersInGroup(a);
        const bProjectCount = getProjectNumbersInGroup(b);
        comparison = bProjectCount - aProjectCount;
        break;
      }
      case "created_date_ascending":
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case "created_date_descending":
        comparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        break;
      default:
        comparison = 0;
    }

    return comparison;
  });

  return sortedGroups;
}

/**
 * Converts a SortItem to a GroupsSortBy string
 * @param sortItem The sort item containing field and sort direction
 * @returns The GroupsSortBy string
 */
export function getGroupsSortBy(sortItem: SortItem<GroupsSortField>): GroupsSortBy {
  const { field, sort } = sortItem;
  const isAscending = sort === "asc";
  
  // Map the GroupHeaders enum values to the GroupsSortBy keys
  switch (field) {
    case GroupHeaders.name:
      return isAscending ? "name_ascending" : "name_descending";
    case GroupHeaders.members:
      return isAscending ? "members_ascending" : "members_descending";
    case GroupHeaders.projects:
      return isAscending ? "projects_ascending" : "projects_descending";
    case GroupHeaders.createdDate:
      return isAscending ? "created_date_ascending" : "created_date_descending";
    default: {
      return DEFAULT_GROUPS_SORT_BY;
    }
  }
}
